// =================================================================================================
// >>> **AKASHIC-1 — THE AKASHIC RECORDS ROOM, HER ORDER OF 2026-09-01.** <<<
//
// The roster the client and the server must AGREE on: which readings exist, what they are called,
// which one needs two people, what tier opens the door, and the one line every reading ends with.
//
// **IT IMPORTS NOTHING** (R146 — reach is transitive), so a `.mjs` gate can load and RUN it with no
// pool, no network and no browser.
//
// **THE FRAMING LAW IS NOT IN THIS FILE, AND THAT IS HER RULING.** *"No 'the house's reading, not
// Lala's method' language appears anywhere on the room, the readings, or the membership tiles; it
// lives in the Terms only and in the code's gate, invisible on the page."* So the disclaimer lives
// in `terms-of-service.md` §2 and in `akashicJargonViolations`, and **there is deliberately no
// member-facing constant here for a later round to helpfully render.** A room that explains itself
// is the room she rejected.
// =================================================================================================

export const AKASHIC_ROOM = "akashic";

/** Her ruling, 2026-09-01: the room opens at Apprentice, all five readings at that tier. */
export const AKASHIC_TIER = "apprentice";

/**
 * >>> **THE DOOR AT THE FOOT OF EVERY READING, TYPED ONCE.** <<< It is rendered by the room, by the
 * saved report on her shelf, and by the drawer that opens from it — three surfaces, one string, so
 * the sentence a member reads can never drift between them (R28/R29).
 */
export const AKASHIC_DOOR_LABEL = "Go deeper into your records with Lala";

/**
 * **THE FIVE, IN HER ORDER.**
 *
 * `twoSlot` marks the only reading in this house that needs two people at once, so the picker asks
 * the roster rather than a caller remembering which one it was.
 *
 * `blurb` is the room's card copy. **Note what none of them say:** no reading is described as
 * "read from your chart", because that is method talk on the room itself, and her ruling put method
 * talk off every member-facing surface — not only out of the readings.
 */
export const AKASHIC_READINGS = [
  {
    key: "soul-record",
    label: "Your Soul Record",
    blurb: "Who you have been, what you carry, and what this life came here to master.",
    twoSlot: false,
  },
  {
    key: "past-life-connections",
    label: "Past-Life Connections",
    blurb: "Whether you and someone in your circle have met before, how you crossed, and what the reunion is for now.",
    twoSlot: true,
  },
  {
    key: "soul-contracts",
    label: "Your Soul Contracts",
    blurb: "The agreements you came in with: the people you keep meeting, the wounds you came to heal, the gifts you carried in.",
    twoSlot: false,
  },
  {
    key: "past-life-gifts",
    label: "Past-Life Gifts and Talents",
    blurb: "What you already mastered before this life, and what comes so easily that you have never given yourself credit for it.",
    twoSlot: false,
  },
  {
    key: "purpose-timeline",
    label: "Your Soul's Purpose Timeline",
    blurb: "When the big lessons and turning points switch on in this life, and what each one is asking of you.",
    twoSlot: false,
  },
];

export const AKASHIC_KEYS = AKASHIC_READINGS.map((r) => r.key);

/** `null` for anything unrostered — never a guess, and never a default reading. */
export function akashicReading(key) {
  const want = String(key || "").trim().toLowerCase();
  return AKASHIC_READINGS.find((r) => r.key === want) || null;
}

export function akashicLabel(key) {
  return akashicReading(key)?.label || "";
}

/**
 * **PRESENCE, NEVER TRUTHINESS** (R53/R136/R137): this decides whether a second person is demanded,
 * and an unrostered key answering "yes" would stall a member on a picker slot she cannot fill.
 */
export function akashicNeedsTwo(key) {
  return akashicReading(key)?.twoSlot === true;
}

/** What the room says when a reading could not be written. Nothing is charged for it. */
export const AKASHIC_UNFINISHED = "That reading could not be finished right now. Nothing was charged for it.";

/**
 * >>> **IT #312 — THE ROOM'S ANSWER, AND THE READING IS IN IT.** <<<
 *
 * `runGeneration` hands back `{ result, savedId }`, and for this room `result` IS the reading. The
 * handler answered `report: gen.result?.report`, a field the reading never has, so from 2026-09-01
 * every Akashic reading was written, saved, and then answered with the words "It is in your room"
 * and nothing drawn. A build that failed carries `{ error }` so its failure is recorded, and it
 * answers the unfinished sentence rather than an empty success.
 */
export function akashicRoomAnswer(gen, { label = "", type = "" } = {}) {
  const reading = gen?.result;
  if (!reading || reading.error || typeof reading.overview !== "string" || !reading.overview.trim()) {
    return { ok: false, message: AKASHIC_UNFINISHED };
  }
  return { ok: true, id: gen.savedId ?? null, title: label, type, report: reading };
}

/**
 * >>> **READINGS 1 AND 4 READ THE SAME SOURCE AND ASK OPPOSITE QUESTIONS — HER RULING, AND IT IS
 * DECLARED HERE RATHER THAN LEFT TO TWO PROMPTS TO REMEMBER SEPARATELY.** <<<
 *
 * *"reading 1 reads the South Node as the habit to retire, reading 4 as the skill to use, same
 * source, opposite question; readings 1 and 3 split as themes-and-patterns versus the specific
 * agreements."*
 *
 * A member who buys all five must never read the same paragraph twice, and the thing that stops
 * that is one declared angle per reading — not four prompts each hoping the others stayed in their
 * lane. **The engine reads this; it does not restate it.**
 */
/**
 * =================================================================================================
 * >>> **THE FIVE LENSES — HER RULING OF 2026-09-02, AND IT IS ENFORCED RATHER THAN SUGGESTED.** <<<
 *
 * Her walk: *"every reading gravitates to the same dominant theme reworded, instead of each reading
 * revealing different information."* She was right, and `AKASHIC_ANGLE` below is why it happened.
 * The angles TOLD the writer to stay in its lane while the GROUNDING handed all five readings the
 * whole chart — so every one of them found the same loudest signal and dressed it differently.
 *
 * **A LENS IS A FILTER ON THE X-RAY, NOT AN INSTRUCTION ABOUT IT.** Each reading is handed only its
 * own designated features and literally cannot see the others'. That is the difference between
 * asking a writer not to repeat itself and making the repetition impossible.
 *
 * `facet` is her third half: variety in the PERSON, not only the theme. Each lens is pointed at a
 * different part of a life — what she carries, who she keeps meeting, what she is good at, when it
 * moves — so a member learns something NEW in every room rather than one truth five ways.
 * =================================================================================================
 */
export const AKASHIC_LENS = {
  "soul-record": {
    // The karmic axis and the central lesson. This is the ONE reading her dominant theme may anchor.
    bodies: ["north node", "south node", "rahu", "ketu", "sun", "moon"],
    houses: [],
    numbers: true,
    design: true,
    facet: "the shape of the whole life: what she carries and what it is for",
    anchorsDominant: true,
  },
  "soul-contracts": {
    // The specific agreements: who she keeps meeting and what she came to heal.
    bodies: ["saturn", "chiron", "juno", "lilith", "venus", "mars"],
    houses: [7, 8, 12],
    // >>> **`never` EXISTS BECAUSE THE HOUSE FILTER LEAKS, AND THE FIXTURE FOUND IT.** <<< A lens
    // takes its own bodies OR anything standing in its houses — and her South Node sits in the 8th,
    // so it walked straight into the Contracts reading and handed it the Soul Record's own signature.
    // A body that anchors another lens is named here and can never arrive by the back door.
    never: ["south node", "north node", "ketu", "rahu"],
    numbers: false,
    design: false,
    facet: "her relationships and the people-types she keeps meeting, and the wounds underneath them",
    anchorsDominant: false,
  },
  "past-life-gifts": {
    // What she already mastered, read as ASSETS only.
    bodies: ["south node", "ketu", "mercury", "jupiter", "neptune", "uranus"],
    houses: [12],
    numbers: true,
    design: true,
    facet: "what she is naturally good at, what comes easily, and what she can be paid for",
    anchorsDominant: false,
  },
  "purpose-timeline": {
    // The WHEN. Timing bodies only, and never a restatement of the what.
    bodies: ["saturn", "jupiter", "north node", "progressed moon", "chiron"],
    houses: [],
    // The timeline reads the NORTH node as a clock, never the south node^s story.
    never: ["south node", "ketu"],
    numbers: false,
    design: false,
    facet: "her timing: what switches on, and when",
    anchorsDominant: false,
  },
  "past-life-connections": {
    // The two-chart synastry only. Both casts, nothing else.
    bodies: null, // null = the whole cast, because a synastry IS the whole of both charts
    houses: [],
    numbers: false,
    design: false,
    facet: "this one bond and what it is asking of her now",
    anchorsDominant: false,
  },
};

export const akashicLens = (key) => AKASHIC_LENS[String(key || "").trim().toLowerCase()] || null;

export const AKASHIC_ANGLE = {
  "soul-record": "THEMES AND PATTERNS. The old habit as something to RETIRE — what it costs her now. "
    + "Never the specific agreements (that is Your Soul Contracts) and never the talent (that is Past-Life Gifts).",
  "past-life-connections": "TWO SOULS. Whether they crossed, how, who held the weight, what is unfinished, "
    + "and what the reunion is for now.",
  "soul-contracts": "THE SPECIFIC AGREEMENTS. The people-types she keeps meeting, the wounds she came to "
    + "heal, the gifts she carried in to give away. Never the broad themes (that is Your Soul Record).",
  "past-life-gifts": "THE SAME OLD LIFE AS READING ONE, ASKED THE OPPOSITE WAY: what she already mastered "
    + "and can USE. Never framed as a habit to retire.",
  "purpose-timeline": "WHEN. The turning points in order, what each is asking. Whole-life turning points "
    + "are named; only the next five years are dated.",
};

/**
 * **HER CONSERVATIVE CALL ON READING 2, LOGGED IN DECISIONS AND DECLARED HERE BECAUSE THE ENGINE
 * MUST OBEY IT.** The second person did not ask for a reading and cannot answer back, so: what the
 * connection is doing to HER is in scope; what is wrong with THEM is not.
 */
export const AKASHIC_THIRD_PARTY_LIMIT =
  "The other person did not ask for this reading and cannot answer back. Say what the connection is "
  + "and what it is doing to the member. Never diagnose the other person's health, mental state, "
  + "motives or private conduct.";
